/**
 * Pinia Authentication & Session Store
 * Path: packages/web/src/stores/auth.ts
 */

import { defineStore } from 'pinia';
import {
  signInWithGoogle,
  signOutAuth,
  loginAsMock,
  onAuthStateChanged,
  type AuthUserState,
  type UserRole,
} from '../services/firebase.ts';

export interface AuthState {
  user: AuthUserState | null;
  idToken: string | null;
  tenantId: string | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => {
    const hasStorage = typeof localStorage !== 'undefined';
    const storedToken = hasStorage ? localStorage.getItem('gateflow_token') : null;
    const storedTenant = hasStorage ? localStorage.getItem('gateflow_tenant_id') : null;
    const storedRole = hasStorage ? (localStorage.getItem('gateflow_role') as UserRole) : null;
    const storedMockSession = hasStorage ? localStorage.getItem('gateflow_mock_session') : null;

    let initialUser: AuthUserState | null = null;
    if (storedMockSession) {
      try {
        const parsed = JSON.parse(storedMockSession);
        initialUser = parsed.user;
      } catch {
        initialUser = null;
      }
    }

    return {
      user: initialUser,
      idToken: storedToken,
      tenantId: storedTenant,
      role: storedRole,
      isAuthenticated: !!storedToken,
      loading: false,
      error: null,
    };
  },

  getters: {
    isSuperAdmin: (state): boolean => state.role === 'super-admin',
    isTenantAdmin: (state): boolean => state.role === 'tenant-admin' || state.role === 'super-admin',
    isOperator: (state): boolean =>
      state.role === 'operator' || state.role === 'tenant-admin' || state.role === 'super-admin',
    currentUser: (state): AuthUserState | null => state.user,
    token: (state): string | null => state.idToken,
    activeTenantId: (state): string => state.tenantId || 'tenant-001',
    userDisplayName: (state): string => state.user?.displayName || state.user?.email || 'Authenticated User',
    userPhoto: (state): string =>
      state.user?.photoURL ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(state.user?.displayName || 'GF')}&background=0284c7&color=fff`,
  },

  actions: {
    /**
     * Authenticate via Google OAuth Popup
     */
    async loginWithGoogle(): Promise<void> {
      this.loading = true;
      this.error = null;
      try {
        const { user, idToken } = await signInWithGoogle();
        this.setUserSession(user, idToken);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Authentication failed';
        this.error = msg;
        throw err;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Switch / Login as Mock User (Operator, Tenant Admin, Super Admin)
     */
    setMockUser(role: UserRole = 'tenant-admin', tenantId: string = 'tenant-001'): void {
      this.loading = true;
      this.error = null;
      try {
        const { user, idToken } = loginAsMock(role, tenantId);
        this.setUserSession(user, idToken);
      } catch (err: unknown) {
        this.error = err instanceof Error ? err.message : 'Failed to set mock user';
      } finally {
        this.loading = false;
      }
    },

    /**
     * Sign in / Switch directly to a realistic enterprise account persona
     */
    loginAsEnterpriseUser(account: { uid: string; name: string; email: string; phone?: string; role: UserRole; tenant_id: string; avatar_url?: string }): void {
      const tenant = account.tenant_id === 'GLOBAL' ? (this.tenantId || 'tenant-001') : account.tenant_id;
      const mockToken = `mock:${account.uid}:${tenant}:${account.role}`;
      const userState: AuthUserState = {
        uid: account.uid,
        email: account.email,
        displayName: account.name,
        photoURL: account.avatar_url || null,
        tenantId: tenant,
        role: account.role,
        isMock: true,
      };
      this.setUserSession(userState, mockToken);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('gateflow_mock_session', JSON.stringify({ user: userState, idToken: mockToken }));
      }
    },

    /**
     * Switch active tenant context (super-admin only)
     */
    switchTenant(newTenantId: string): void {
      this.tenantId = newTenantId;
      if (this.user) {
        this.user.tenantId = newTenantId;
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('gateflow_tenant_id', newTenantId);
        if (this.user?.isMock) {
          const mockToken = `mock:${this.user.uid}:${newTenantId}:${this.role || 'super-admin'}`;
          this.idToken = mockToken;
          localStorage.setItem('gateflow_token', mockToken);
          localStorage.setItem('gateflow_mock_session', JSON.stringify({ user: this.user, idToken: mockToken }));
        }
      }
    },

    /**
     * Internal session setter
     */
    setUserSession(user: AuthUserState, idToken: string): void {
      this.user = user;
      this.idToken = idToken;
      this.tenantId = user.tenantId;
      this.role = user.role;
      this.isAuthenticated = true;

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('gateflow_token', idToken);
        localStorage.setItem('gateflow_tenant_id', user.tenantId);
        localStorage.setItem('gateflow_role', user.role);
      }
    },

    /**
     * Sign out and clear stored session
     */
    async logout(): Promise<void> {
      this.loading = true;
      try {
        await signOutAuth();
      } finally {
        this.user = null;
        this.idToken = null;
        this.tenantId = null;
        this.role = null;
        this.isAuthenticated = false;
        this.error = null;
        this.loading = false;

        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('gateflow_token');
          localStorage.removeItem('gateflow_tenant_id');
          localStorage.removeItem('gateflow_role');
        }
      }
    },

    /**
     * Initialize auth listener on application bootstrap
     */
    initAuth(): Promise<void> {
      return new Promise((resolve) => {
        onAuthStateChanged((user, idToken) => {
          if (user && idToken) {
            this.setUserSession(user, idToken);
          } else if (typeof localStorage !== 'undefined' && !localStorage.getItem('gateflow_token')) {
            this.user = null;
            this.idToken = null;
            this.tenantId = null;
            this.role = null;
            this.isAuthenticated = false;
          }
          resolve();
        });
      });
    },
  },
});
